import {
  useGetCalculationProgress,
  useGetConceptCalculationYears,
  useGetOngoingCalculations,
  usePromoteConceptCalculation,
  useStartCalculationProcess,
} from '@/lib/queries/calculation-api/calculation';
import { CalculationProgress, CalculationStartResult } from '@/lib/types/calculation';
import { Alert, Button, Card, Flex, message, Modal, Progress, Select, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const getAllowedYears = (): number[] => {
  const retentionYears = 3; // RecalculationRetentionYears default
  const currentYear = new Date().getUTCFullYear();
  return Array.from({ length: retentionYears }, (_, i) => currentYear - retentionYears + 1 + i).sort((a, b) => b - a); // Sort descending
};

export const Rekenregels: React.FunctionComponent = () => {
  const COMPLETED_STATUS = 'completed';

  const { t } = useTranslation(['core', 'errors']);

  const [ongoing, setOngoing] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<CalculationProgress | undefined>();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();

  const { data: ongoingCalculationId, isLoading: isLoadingOngoing, isError: isOngoingErorr } = useGetOngoingCalculations();
  const { data: progressData, isError: isProgressError } = useGetCalculationProgress(ongoing);
  const { data: conceptYears = [], refetch: refetchConceptYears } = useGetConceptCalculationYears();
  const startCalculations = useStartCalculationProcess();
  const promoteConceptCalculation = usePromoteConceptCalculation();

  // It may take a few seconds for the system to start the calculation process which is why the progress
  // endpoint may return a 404 the first time calling it.
  const taskIsPending = (): boolean => ongoing !== undefined && isProgressError;

  const hasError = (): boolean => (isOngoingErorr || isProgressError) && !taskIsPending();

  useEffect(() => {
    setIsLoading(isLoadingOngoing);
  }, [isLoadingOngoing]);

  useEffect(() => {
    setOngoing(ongoingCalculationId?.id);
    setIsLoading(false);
  }, [ongoingCalculationId]);

  useEffect(() => {
    setProgress(progressData);
    setIsLoading(false);
    if (progressData?.status === COMPLETED_STATUS) {
      refetchConceptYears();
    }
  }, [progressData, refetchConceptYears]);

  const startCalculation = (concept: boolean) => {
    if (!selectedYear) {
      return;
    }

    setOngoing(undefined);
    setProgress(undefined);
    setIsLoading(true);

    startCalculations.mutate(
      { registratiejaar: selectedYear, concept },
      {
        onSuccess: (res: CalculationStartResult) => {
          setOngoing(res.id);
        },
        onSettled: () => {
          setIsLoading(false);
        },
      }
    );
  };

  const berekenen = (concept: boolean) => {
    if (!selectedYear) {
      return;
    }

    Modal.confirm({
      title: t('rekenregels.warningTitle'),
      content: concept ? t('rekenregels.conceptWarningMessage') : t('rekenregels.warningMessage'),
      okText: t('rekenregels.confirmStart'),
      cancelText: t('rekenregels.cancelStart'),
      onOk: () => startCalculation(concept),
    });
  };

  const promoteConcept = (registratiejaar: number) => {
    Modal.confirm({
      title: t('rekenregels.promoteConceptTitle', { year: registratiejaar }),
      content: t('rekenregels.promoteConceptWarningMessage'),
      okText: t('rekenregels.promoteConceptConfirm'),
      cancelText: t('rekenregels.cancelStart'),
      onOk: () =>
        promoteConceptCalculation.mutateAsync(registratiejaar, {
          onSuccess: () => message.success(t('rekenregels.promoteConceptSuccess', { year: registratiejaar })),
        }),
    });
  };

  const yearOptions = getAllowedYears().map(year => ({ value: year, label: year.toString() }));
  const progressStatus = progress?.status === COMPLETED_STATUS ? 'success' : 'active';
  const progressStatusWithError = hasError() ? 'exception' : progressStatus;
  const isCalculationRunning = !!ongoing && progress?.status !== COMPLETED_STATUS;

  return (
    <Card title={t('rekenregels.title')} loading={isLoading}>
      <Space orientation="vertical" size="small" style={{ display: 'flex' }}>
        {progress?.status && t(progress.status, { keyPrefix: 'rekenregels.statusses' })}
        {hasError() && <Alert description={t('rekenregels.somethingWentWrong', { ns: 'errors' })} type="error" showIcon />}
        {conceptYears.map(year => (
          <Alert
            key={year}
            type="warning"
            showIcon
            description={
              <Space orientation="vertical" size={0}>
                <Typography.Text strong>{t('rekenregels.openConceptTitle', { year })}</Typography.Text>
                <Typography.Text>{t('rekenregels.openConceptDescription')}</Typography.Text>
              </Space>
            }
            action={
              <Button size="small" disabled={isCalculationRunning || promoteConceptCalculation.isPending} onClick={() => promoteConcept(year)}>
                {t('rekenregels.promoteConcept')}
              </Button>
            }
          />
        ))}
        <Space orientation="vertical" size="small" style={{ display: 'flex' }}>
          <Typography.Text>{t('rekenregels.selectYear')}</Typography.Text>
          <Select
            value={selectedYear}
            options={yearOptions}
            placeholder={t('rekenregels.selectYearPlaceholder')}
            onChange={setSelectedYear}
            style={{ width: 220 }}
            disabled={isCalculationRunning}
          />
        </Space>
        <Progress percent={progress?.progress} status={progressStatusWithError} />
        {isCalculationRunning && <Alert description={t('rekenregels.info')} type="info" showIcon />}
        <Flex style={{ paddingTop: 15 }} justify="end" gap="small">
          <Button disabled={!selectedYear || isCalculationRunning} onClick={() => berekenen(true)}>
            {t('rekenregels.herberekenenRapportagesConcept')}
          </Button>
          <Button type="primary" disabled={!selectedYear || isCalculationRunning} onClick={() => berekenen(false)}>
            {t('rekenregels.herberekenenRapportages')}
          </Button>
        </Flex>
      </Space>
    </Card>
  );
};
